import aws4 from 'aws4';
import Config from 'Config';
import getCredentials from 'utils/getCredentials';

/**
 * SigV4-sign a WebSocket URL by putting the signature in the query string.
 *
 * ## Why this exists instead of `createSignedRequest`
 *
 * Every other call in the app signs with the signature in an `Authorization`
 * header. That is impossible here: the browser API is
 * `new WebSocket(url, protocols)` and has no header parameter — it is a
 * limitation of the WebSocket spec, not of any library. SigV4's other mode puts
 * the signature in the query string (the same mechanism as an S3 presigned
 * URL), which is what `aws4`'s `signQuery` does.
 *
 * Only the `host` header ends up in `SignedHeaders`, and the browser sets
 * `Host` itself and correctly — which is exactly why this works at all.
 *
 * ## `X-Amz-Expires`
 *
 * `aws4` only adds `X-Amz-Expires` for the `s3` service, so for `execute-api`
 * we put it in the path *before* signing, where it is preserved and folded into
 * the signature. Whether API Gateway actually requires it on a WebSocket
 * `$connect` is one of the questions `WsIamSpikeStack` exists to answer; setting
 * it is harmless either way, and a short window is the right default for a
 * handshake that happens immediately.
 *
 * ## Consistency
 *
 * The returned URL is rebuilt from `signed.path`, so what we connect to is
 * always byte-identical to what was signed. This matters more than it looks:
 * `REACT_APP_CHATBOT_WEBSOCKET_URL` has a trailing slash on prod and none on
 * dev, and signing `/prod` while connecting to `/prod/` is a 403.
 *
 * @param {string} wssUrl e.g. `wss://abc123.execute-api.eu-west-1.amazonaws.com/dev`
 * @param {object} [options]
 * @param {number} [options.expiresInSeconds=60] Signature validity window.
 * @param {object} [options.credentials] Override; otherwise fetched.
 * @returns {Promise<string>} A `wss://` URL ready for `new WebSocket(...)`.
 */
export const createSignedWebSocketUrl = async (wssUrl, options = {}) => {
  const { expiresInSeconds = 60, credentials: providedCredentials } = options;

  if (!wssUrl) {
    throw new Error('createSignedWebSocketUrl: wssUrl is required');
  }

  const { host, path } = parseWssUrl(wssUrl);

  const credentials = providedCredentials || (await getCredentials());
  const { accessKeyId, secretAccessKey, sessionToken } = credentials;

  // X-Amz-Expires has to be present before signing so it is covered by the
  // signature rather than tacked on afterwards (which would invalidate it).
  const pathWithExpiry = `${path}?X-Amz-Expires=${expiresInSeconds}`;

  const signer = new aws4.RequestSigner(
    {
      host,
      path: pathWithExpiry,
      method: 'GET',
      service: 'execute-api',
      region: Config.AWS_REGION,
      signQuery: true,
      // Anything added here would land in SignedHeaders, and the browser cannot
      // send it. Leave empty — aws4 signs `host` on its own.
      headers: {},
    },
    { accessKeyId, secretAccessKey, sessionToken }
  );

  const signed = signer.sign();

  return `wss://${host}${signed.path}`;
};

/**
 * Split a ws/wss URL into the host and the path SigV4 needs.
 *
 * A trailing slash is dropped so dev and prod sign identically; `URL` gives us
 * `/` for an empty path, which is also the correct canonical URI.
 */
const parseWssUrl = (wssUrl) => {
  // `URL` does not treat wss: as a special scheme everywhere, so normalise to
  // https: for parsing only.
  const parsed = new URL(wssUrl.replace(/^wss:/i, 'https:').replace(/^ws:/i, 'http:'));

  const path =
    parsed.pathname.length > 1
      ? parsed.pathname.replace(/\/+$/, '')
      : '/';

  return { host: parsed.host, path };
};

export default createSignedWebSocketUrl;
